import { ProviderBrandAttribute } from '@wisereducacao-npm/design-system-brands-web/dist/components/provider/types';

// hooks
import { ProductThemeProps } from 'hooks/useCart';
import { allAvailableProductThemes } from 'hooks/useProductTheme';

// types
import { Business, PlanType } from 'types';
import {
  ProductFlowSteps,
  ProductFlowTemplates,
  ProductPlan,
} from './definitions';
import { LeadFlowStep } from './steps/lead.step';

export const pageTemplates = [
  { id: 'lead', route: '/lead' },
  { id: 'gift', route: '/brinde' },
  { id: 'phone-verification', route: '/phone-verification' },
  { id: 'video', route: '/video' },
  { id: 'upsell-video', route: '/video/upsell' },
  { id: 'order-bump-presentation', route: '/multiprofile-presentation' },
  { id: 'order-bump', route: '/order-bump-presentation' },
  { id: 'presentation', route: '/presentation' },
  {
    id: 'upsell-order-bump-presentation',
    route: '/multiprofile-upsell-presentation',
  },
  { id: 'payment', route: '/payment' },
  { id: 'custom-offer', route: '/custom-offer' },
  { id: 'success', route: '/success' },
  { id: 'upsell-payment', route: '/payment/upsell' },
  { id: 'upsell-success', route: '/success/upsell' },
  { id: 'pix', route: '/pix' },
  { id: 'livelo-cpf', route: '/livelo/cpf' },
  { id: 'invoice-payment', route: '/payment/[id]' },
  { id: 'recovery', route: '/recovery/[id]' },
  { id: 'renew', route: '/renew/[id]' },
  { id: 'error404', route: '/generic-error' },
  { id: 'error', route: '/generic-error' },
  { id: 'select-school', route: '/select-school' },
  { id: 'upgrade-payment', route: '/payment/upgrade' },
  { id: 'md', route: '/md' },
  { id: 'select-test-location', route: '/select-test-location' },
];

export type ProductFlowStep = {
  defaultNextStepId: ProductFlowSteps;
  id: ProductFlowSteps;
  props?: {
    /**
     * Permite exibição do texto: "com desconto" em todas as parcelas, exceto última
     * pasando por cima das demais regras existentes
     */
    shouldDisplayWithDiscountOnAllInstallments?: boolean;

    /**
     * Texto para uso dos termos (Termos de Uso, Termos de Contrato, Termos de Compra, ou outra personalização)
     */
    termsContent?: string;
    terms?: { label: string; url: string }[];
    /**
     * Permite que o usuário troque o cupom que for inserido na tela de pagamento
     */
    canUserChangeCoupon?: boolean;
    /**
     * Lista de cidades com provas presenciais
     */
    cities?: string[];
    /**
     * Tipos de teste das provas presenciais
     */
    exams?: string[];
    /**
     * Quando true, restringe a listagem de estados/cidades/unidades apenas às
     * escolas habilitadas para venda na recorrência (ex.: link de venda recorrente).
     * Se não existir, o filtro não é enviado ao backend e o comportamento atual é mantido.
     */
    recurringSaleAllowed?: boolean;
    /**
     * Quando true, realiza uma análise de crédito do usuário antes de permitir a compra, e caso o mesmo não seja aprovado, exibe uma mensagem de erro e não permite a compra.
     */
    creditAnalysisAllowed?: boolean;
    /**
     * Exibe a comunicação do checkbox de "Termos do contrato" na tela de pagamento
     */
    showContractTermsCommunicationCheckbox?: boolean;
    termsUrl?: string;
    hideUpsellLogo?: boolean;
    politicsUrl?: string;
    enableAllDocumentsForInvoice?: boolean;
    countdownTimerSeconds?: number;
    enableInstallmentsForAllPaymentMethods?: boolean;
    /**
     * Habilita a request de documento na para geração de NF
     */
    shouldRequestDocument?: boolean;
    /**
     * Força o tipo de documento solicitado (cpf, cnpj, dni)
     */
    documentType?: string;
    /**
     * Habilita a geração externa de contrato na tela de pagamento
     */
    useCheckboxContractTerms?: boolean;
    /**
     * Link para FAQ usando em mensagens de erro
     */
    faqLink: string;
    orderBump?: {
      plan: {
        id: string;
        productCode: string;
        /**
         * A ideia futura é removermos o plan, planType, business e productCode,
         * deixando apenas o planId
         */
        plan: string;
        planType: PlanType;
        business: Business;
      };
      content: {
        logo: string;
        cardText: string;
        cardResume: string;
        checkboxText: string;
      };
    };
    displayContractCheckbox: boolean;
    /**
     * @deprecated
     * estamos migrando incrementalmente para que a configuração dos métodos
     * de pagamento seja realizada no Sales Force, a nivel do plano, e não mais no checkout-flows.
     */
    paymentMethods: { id: string }[];
    /**
     * @description
     * Caso essa prop seja setada, vai sobrescrever os métodos de pagamento vindo
     * do plano do produto.
     */
    paymentMethodsOverride: { id: string }[];
    showBirthDateInput: boolean;
    showDocumentInput: boolean;
    documentTypeInput?: 'cpf' | 'cnpj' | 'dni';
    showResponsibleInputs: boolean;
    hideTermsUrl: boolean;
    descriptionTitle?: string;
    description: string;
    secondaryButtonLabel?: string;
    primaryButtonLabel?: string;
    media: {
      source: string;
      logoCard?: string;
    };
    upsell?: {
      description?: string;
      hideSecondaryButton?: boolean;
      infoText?: string;
      offerDisplayName?: string;
      priceSummary?: string;
    };
    cardThumbnail: string;
    cardDescription: string;
    buttonLabel: string;
    icon: string;
    subtitle: string;
    subtitle2: string;
    title: string;
    productTitle: string;
    productDescription: string;
    productDisplayName: string;
    plan: ProductPlan;
    plans: ProductPlan[];
    /**
     * FIN-7199 (Parte 10): gate + config do fluxo de entrada + boleto
     * parcelado do MD. A PRESENÇA deste objeto no step `main-payment` é o
     * gatilho do fluxo combinado forçado (entrada + boleto) — não mais
     * `plan.paymentMethods.includes('bank-slip-installment')`.
     */
    mdWithRecurrencyConfig?: {
      /** Piso da entrada (cartão/Pix), em percentual (ex.: 27 = 27%). */
      upfrontPaymentPercentage: number;
      /** Teto de parcelas oferecidas no box de boleto. */
      maxBilletQuantity: number;
      /** Regras de vencimento da 1ª parcela do boleto. */
      dueDateRules: {
        /** Máximo de dias a partir de hoje para a 1ª parcela vencer. */
        maxDays: number;
        /** Mínimo de dias a partir de hoje para a 1ª parcela vencer. */
        minDays: number;
        /** Dias do mês aceitos para o vencimento (ex.: [5, 15, 25]). */
        allowedDays: number[];
      };
    };
    voucher?: string;
    redirect?: string;
    displayVoucherInput?: boolean;
    forceDisplayVoucherInput?: boolean;
    displayRenewAutomaticallyCheckbox: boolean;
    business: Business;
    productCode: string;
    hideButton: boolean;
    defaultSelectedInstallment: number;
    removeCardBorder?: boolean;
    useNeutralPureColorForText?: boolean;
    useNewPlansFormat?: boolean;
    externalLandingPageOffer?: {
      src: string;
    };
    externalPaymentPage?: {
      src: string;
    };
  };
  templateId: ProductFlowTemplates;
} & LeadFlowStep;

export interface ProductFlowProps {
  id: string;
  initialStep: ProductFlowSteps;
  version: string;
  query: {
    [key: string]: string;
  };
  steps: ProductFlowStep[];
  props: {
    config: {
      /**
       * Every first offer steps number
       */
      stepsNumber: number;
    };
    main: {
      business: string;
      productCode: string;
      logo: string;
      name: string;
      dsProductId: ProviderBrandAttribute;
    };
    upsell?: {
      business: string;
      productCode: string;
      logo: string;
      name: string;
      presentationImg: string;
      multiProfilePresentationImg?: string;
      displayName?: string;
      priceSummary?: string;
      dsProductId: ProviderBrandAttribute;
      dsMode: 'dark' | 'light';
      /**
       * Se existir esse parâmetro, entende-se que deseja redirecionar
       * o usuário para uma conversa no whatsapp ao clicar na oferta do upsell
       */
      whatsAppRedirect?: string;
      /**
       * Quando true, bloqueia a modificação de itemsQuantity no upsell,
       * usando o valor que foi pago na tela de pagamento anterior.
       */
      lockItemsQuantity?: boolean;
    } | null;
    shouldApplyBlackNovemberImplementation?: boolean;
    media: {
      logo: string;
      favicon: string;
    };
    style: {
      logo: {
        width: number;
      };
    };
  };
}

interface GetCurrentStepProps {
  productFlow: ProductFlowProps;
  pathname: string;
}

export const getCurrentStep = ({
  productFlow,
  pathname = '',
}: GetCurrentStepProps) => {
  const currentTemplate = pageTemplates.find((item) => item.route === pathname);
  const currentStepProps = productFlow?.steps?.find(
    (step) => step.templateId === currentTemplate?.id
  );

  return {
    ...currentStepProps,
    route: currentTemplate?.route,
  };
};

interface GetNextStepProps extends GetCurrentStepProps {}

export const getNextStep = ({
  productFlow,
  pathname = '',
}: GetNextStepProps) => {
  const currentStepProps = getCurrentStep({
    productFlow,
    pathname,
  });

  const nextStepProps = productFlow?.steps.find(
    (item) => item.id === currentStepProps?.defaultNextStepId
  );
  const nextTemplate = pageTemplates.find(
    (item) => item.id === nextStepProps?.templateId
  );

  return {
    ...nextStepProps,
    route: nextTemplate?.route,
  };
};

interface GetUpsellThemeByProductFlowProps {
  productFlow: ProductFlowProps;
}

/**
 * @deprecated
 *
 * use getUpsellThemeByProductFlowNew instead this
 */
export const getUpsellThemeByProductFlow = ({
  productFlow,
}: GetUpsellThemeByProductFlowProps) => {
  const upsellDsProductId = productFlow?.props?.upsell?.dsProductId;

  const upsellTheme = allAvailableProductThemes?.find((theme) => {
    const themeData = theme as unknown as ProductThemeProps;

    return themeData?.data?.dsProductId === upsellDsProductId;
  });

  return upsellTheme as unknown as ProductThemeProps;
};

export const getProductFlowStepById = (
  stepId: ProductFlowSteps,
  productFlow: ProductFlowProps
) => {
  return productFlow?.steps?.find((step) => step?.id === stepId);
};
